/**
 * 配置 schema（zod）—— 场景 / 客流 / 实验配置的单一事实来源（设计文档 D8）。
 *
 * UI 表单、导入文件、内核入口三处共用同一 schema，非法配置在内核启动前被拦截（NF-008）。
 * 类型由 schema 推导，避免手写类型与校验逻辑漂移。
 */
import { z } from 'zod';

export const ElevatorConfigSchema = z.object({
  id: z.string().min(1),
  capacity: z.number().int().positive('轿厢容量须为正整数'),
  ratedSpeed: z.number().positive('额定速度须为正'), // m/s
  acceleration: z.number().positive('加速度须为正'), // m/s²
  doorOpenTime: z.number().int().nonnegative(), // ms
  doorCloseTime: z.number().int().nonnegative(), // ms
  boardTimePerPax: z.number().int().nonnegative(), // ms
  alightTimePerPax: z.number().int().nonnegative(), // ms
  carMass: z.number().positive().default(1500), // kg，能耗估算用
  initialFloor: z.number().int().min(0).default(0),
});
export type ElevatorConfig = z.infer<typeof ElevatorConfigSchema>;

export const SceneConfigSchema = z.object({
  floors: z.number().int().min(2, '楼层数最小为 2').max(120, '楼层数最大为 120'),
  floorHeight: z.number().positive('层高须为正'), // 米
  lobbyFloor: z.number().int().min(0).default(0),
  elevators: z.array(ElevatorConfigSchema).min(1, '至少 1 台电梯'),
});
export type SceneConfig = z.infer<typeof SceneConfigSchema>;

export const TrafficSegmentSchema = z.object({
  startSec: z.number().int().min(0),
  endSec: z.number().int().positive(),
  arrivalRatePer5min: z.number().nonnegative(), // 人 / 5 分钟
});
export type TrafficSegment = z.infer<typeof TrafficSegmentSchema>;

export const TrafficModeSchema = z
  .object({
    upPeak: z.number().min(0).max(1),
    downPeak: z.number().min(0).max(1),
    interfloor: z.number().min(0).max(1),
  })
  .refine((d) => Math.abs(d.upPeak + d.downPeak + d.interfloor - 1) < 1e-9, {
    message: '交通模式比例之和须为 100%',
  });
export type TrafficMode = z.infer<typeof TrafficModeSchema>;

export const TrafficConfigSchema = z.object({
  profile: z.array(TrafficSegmentSchema).min(1, '至少 1 个时段'),
  mode: TrafficModeSchema,
});
export type TrafficConfig = z.infer<typeof TrafficConfigSchema>;

export const ExperimentConfigSchema = z.object({
  scene: SceneConfigSchema,
  traffic: TrafficConfigSchema,
  seed: z.number().int(),
});
export type ExperimentConfig = z.infer<typeof ExperimentConfigSchema>;

/** 从场景配置派生运动学参数（内核调度与渲染插值共用）。 */
export function kinematicsFromScene(scene: SceneConfig, elev: ElevatorConfig) {
  return {
    floorHeight: scene.floorHeight,
    ratedSpeed: elev.ratedSpeed,
    acceleration: elev.acceleration,
  };
}
